// The course's own state, under <hat>/cooking. One node per lesson:
//   plannedFor   — 'YYYY-MM-DD' the night it is meant to be cooked
//   orderedAt    — when the creamery order was marked placed
//   listedAt     — (until 2026-10-02) when the old button put the rest on the
//                  Meal Hat list; lesson nights now carry their own ingredients
//   startedAt    — cook mode opened
//   cookedAt     — cook mode finished
//   debrief      — { transcript, summary, at }
//   live         — cook mode's position, shared between devices (utils/liveCook.js); gone when cooked
//   trace        — what happened during the meal (utils/trace.js)
// Beside the lessons: chat, teacher/notes, preferences, builds (utils/teacher.js)
// and recipes/<id>, the household's copy of each lesson (utils/recipes.js).
// Nothing here is derived; every field is a thing that happened.
import { dbGet, dbPatch } from '../store/db.js';

export const coursePath = (hat) => `${hat}/cooking`;

export async function loadProgress (hat) {
  try {
    return (await dbGet(`${coursePath(hat)}/lessons`)) || {};
  } catch {
    return {};
  }
}

export function markLesson (hat, lessonId, fields) {
  return dbPatch(`${coursePath(hat)}/lessons/${lessonId}`, fields);
}

// The week's plan, keyed by the Thursday it starts:
//   weeks/<thursday> = { lessons: { <lessonId>: 'YYYY-MM-DD' }, plannedAt, orderedAt, listedAt }
export async function loadWeek (hat, key) {
  try {
    return (await dbGet(`${coursePath(hat)}/weeks/${key}`)) || null;
  } catch {
    return null;
  }
}

export function saveWeek (hat, key, fields) {
  return dbPatch(`${coursePath(hat)}/weeks/${key}`, fields);
}

export const todayISO = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * The weekly loop, from the household's cycle (Matt, 2026-09-23): the
 * creamery order goes in by Monday, arrives Wednesday night or Thursday
 * morning, the lesson is cooked between Thursday and Sunday.
 */
export function weekPlan (now = new Date()) {
  const day = now.getDay(); // 0 Sun … 6 Sat
  const daysToMonday = (1 - day + 7) % 7;
  const monday = new Date(now); monday.setDate(now.getDate() + daysToMonday);
  const thursday = new Date(monday); thursday.setDate(monday.getDate() + 3);
  const suggested = new Date(thursday);
  // Weekend lessons default to Saturday; weeknight ones to Thursday.
  return {
    orderBy: monday,
    arrives: thursday,
    weeknight: suggested,
    weekend: new Date(thursday.getTime() + 2 * 86400000),
    // Past Monday's cutoff, an order lands the week after.
    orderIsThisWeek: daysToMonday > 0 || day === 1
  };
}

// A bare 'YYYY-MM-DD' is read at local noon: `new Date('2026-10-02')` is UTC
// midnight, which is the evening BEFORE anywhere west of Greenwich — tonight's
// lesson would have read as yesterday. Timestamps (cookedAt) go straight in.
export const shortDate = (date) => {
  if (!date) return '';
  const value = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00` : date;
  return new Date(value).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

/** The debrief summaries so far, oldest first, for the next lesson to learn from. */
export function debriefHistory (progress = {}, lessons = []) {
  return lessons
    .map((lesson) => ({ lesson, entry: progress[lesson.id] }))
    .filter(({ entry }) => entry?.debrief?.summary)
    .map(({ lesson, entry }) => ({ lessonId: lesson.id, title: lesson.title, dish: lesson.dish, summary: entry.debrief.summary, at: entry.debrief.at || null }));
}
